import { array, string } from 'fp-ts';
import { CommanderThunkAction } from '@mews-commander/types';
import { getUrlParameters } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { fetchCities, getCityIds } from '@mews-commander/legacy-entities/src/ducks/cities';
import { fetchActiveBookableServices } from '@mews-commander/legacy-entities/src/actions/services';
import { fetchAvailabilityBlocks } from '@mews-commander/legacy-entities/src/actions/availabilityBlocks';
import {
    TIME_TO_RESERVATION_START_MARK,
    NRS_LOAD_END_MARK,
    TIME_TO_NRS_LOAD,
    NEW_RESERVATION_SCREEN_EVENT_CATEGORY,
} from '../constants';
import {
    getAvailableEnterpriseIds,
    getCurrentUserAvailableCityIds,
    getCurrentUserAvailableEnterpriseIds,
    shouldShowReservationPlaceSelector,
} from '../selectors/entities';
import { NewReservationScreenUrlParams } from '../schemas/urlParamsSchema';
import { selectReservationPlace } from './place';
import { processUrlParams } from './params';

export const fetchNewReservationScreenDependencies =
    (): CommanderThunkAction<Promise<unknown>> => async (dispatch, getState) => {
        window.performance.mark(TIME_TO_RESERVATION_START_MARK);

        const state = getState();
        // @ts-expect-error $TSFixMeError: Expected 0 arguments, but got 1.
        const availableCityIds = getCurrentUserAvailableCityIds(state);
        const loadedCityIds = getCityIds(state);
        const cityIds = array.difference(string.Eq)(loadedCityIds)(availableCityIds);

        const currentEnterpriseId = getCurrentEnterpriseId();
        const showReservationPlaceSelector = shouldShowReservationPlaceSelector(state);

        const enterpriseIds = showReservationPlaceSelector
            ? getCurrentUserAvailableEnterpriseIds(state)
            : getAvailableEnterpriseIds(state);

        const { AvailabilityBlockId: availabilityBlockId } = getUrlParameters(new URL(global.location.href));

        return Promise.all([
            ...(array.isNonEmpty(cityIds) ? [dispatch(fetchCities({ cityIds }))] : []),
            dispatch(fetchActiveBookableServices({ enterpriseIds, extent: { services: true } })),
            ...(availabilityBlockId
                ? [
                      dispatch(
                          fetchAvailabilityBlocks({
                              availabilityBlockIds: [availabilityBlockId as string],
                              enterpriseIds: [currentEnterpriseId],
                              extent: {
                                  adjustments: true,
                                  profiles: false,
                                  serviceOrders: false,
                              },
                          })
                      ),
                  ]
                : []),
        ]);
    };

export const initNewReservationScreen =
    (searchParams: NewReservationScreenUrlParams, scope: string): CommanderThunkAction =>
    async (dispatch, getState, { loggerService }) => {
        const enterpriseId = getCurrentEnterpriseId();

        try {
            await Promise.all([
                // select your current enterprise as a reservation place
                // this limits initial number of api requests in multi-enterprise accounts
                dispatch(selectReservationPlace(enterpriseId, scope)),
                dispatch(processUrlParams(searchParams, scope)),
            ]);
        } finally {
            window.performance.mark(NRS_LOAD_END_MARK);
            window.performance.measure(TIME_TO_NRS_LOAD, TIME_TO_RESERVATION_START_MARK, NRS_LOAD_END_MARK);

            const [{ duration }] = window.performance.getEntriesByName(TIME_TO_NRS_LOAD);
            loggerService.log(NEW_RESERVATION_SCREEN_EVENT_CATEGORY, TIME_TO_NRS_LOAD, { duration });

            window.performance.clearMarks(NRS_LOAD_END_MARK);
            window.performance.clearMeasures(TIME_TO_NRS_LOAD);
        }
    };
